import React, { useEffect, useRef, useState } from 'react';
import { formatNumber } from './numberFormat';

// Row selection for a paginated, server-filtered list table
// (ui-architecture.md "Bulk Select & Bulk Edit"). Two modes:
//   - explicit: a Set of picked ids, kept across page changes.
//   - allMatching: "every row matching the current filter", across every
//     page — no ids held client-side; the bulk request sends the filter
//     instead, and the server resolves it with the list's own filter logic.
// `resetKey` is any value that changes when the filter/search does (e.g.
// JSON.stringify of the list's params minus page) — a selection made under
// one filter never silently carries over to another.
export function useBulkSelection({ pageIds, total, resetKey }) {
  const [selected, setSelected] = useState(() => new Set());
  const [allMatching, setAllMatching] = useState(false);

  const clear = () => { setSelected(new Set()); setAllMatching(false); };

  useEffect(() => {
    clear();
    // eslint-disable-next-line
  }, [resetKey]);

  const isSelected = (id) => allMatching || selected.has(id);
  const pageSelectedCount = pageIds.filter(isSelected).length;
  const pageState = pageIds.length > 0 && pageSelectedCount === pageIds.length
    ? 'all'
    : (pageSelectedCount > 0 ? 'some' : 'none');

  // Unticking a row while in allMatching mode drops back to explicit mode,
  // keeping the rest of the current page ticked (Gmail's behaviour) — the
  // only ids the client actually knows.
  const toggle = (id) => {
    if (allMatching) {
      setAllMatching(false);
      setSelected(new Set(pageIds.filter((x) => x !== id)));
      return;
    }
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const togglePage = () => {
    if (pageState === 'all') {
      if (allMatching) { clear(); return; }
      setSelected((prev) => {
        const next = new Set(prev);
        pageIds.forEach((id) => next.delete(id));
        return next;
      });
      return;
    }
    setSelected((prev) => new Set([...prev, ...pageIds]));
  };

  const selectAllMatching = () => setAllMatching(true);

  const count = allMatching ? total : selected.size;

  // The bulk endpoint's target: explicit ids, or the list's current filter
  // params (never page/limit/sort — the server resolves every match).
  const target = (filterParams) => (allMatching ? { filter: filterParams } : { ids: [...selected] });

  return { count, total, pageIds, allMatching, isSelected, pageState, toggle, togglePage, selectAllMatching, clear, target };
}

const CHECKBOX_STYLE = { width: 16, height: 16, cursor: 'pointer', margin: 0, verticalAlign: 'middle' };

// Header checkbox — ticks/unticks the current page, showing indeterminate
// when only some of the page is ticked.
export function SelectPageCheckbox({ selection }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = selection.pageState === 'some';
  }, [selection.pageState]);
  return (
    <input
      ref={ref}
      type="checkbox"
      aria-label="Select this page"
      title="Select this page"
      checked={selection.pageState === 'all'}
      disabled={selection.pageIds.length === 0}
      onChange={selection.togglePage}
      style={CHECKBOX_STYLE}
    />
  );
}

// Row checkbox — its <td> needs onClick={(e) => e.stopPropagation()} so a
// tick doesn't also fire the row's click-to-edit.
export function SelectRowCheckbox({ selection, id }) {
  return (
    <input
      type="checkbox"
      aria-label="Select row"
      checked={selection.isSelected(id)}
      onChange={() => selection.toggle(id)}
      style={CHECKBOX_STYLE}
    />
  );
}

const LINK_STYLE = { background: 'none', border: 'none', color: '#1976d2', textDecoration: 'underline', cursor: 'pointer', fontSize: 13, padding: 0 };

// "N selected · <actions> · Clear" bar, shown only while something is
// selected, plus the "Select all N matching" extension once the whole page
// is ticked and more rows exist beyond it. `noun` is the plural record name
// ("Products"); `actions` are [{ key, label, onClick }].
export function BulkSelectBar({ selection, noun, actions }) {
  if (selection.count === 0) return null;
  const { count, total, pageIds, allMatching, pageState } = selection;
  const canExtend = !allMatching && pageState === 'all' && total > pageIds.length;

  return (
    <div style={{ margin: '12px 24px 0', padding: '8px 12px', background: '#eef4fc', border: '1px solid #bcd6f5', borderRadius: 6, fontSize: 13, color: '#1c4e80', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
      <strong>{formatNumber(count)} selected</strong>
      {actions.map((a) => (
        <React.Fragment key={a.key}>
          <span aria-hidden="true">·</span>
          <button
            type="button"
            onClick={a.onClick}
            style={{ padding: '4px 12px', border: 'none', borderRadius: 6, background: '#1976d2', color: '#fff', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}
          >
            {a.label}
          </button>
        </React.Fragment>
      ))}
      <span aria-hidden="true">·</span>
      <button type="button" onClick={selection.clear} style={LINK_STYLE}>Clear</button>
      {canExtend && (
        <span style={{ marginLeft: 6 }}>
          All {pageIds.length} on this page are selected.{' '}
          <button type="button" onClick={selection.selectAllMatching} style={LINK_STYLE}>
            Select all {formatNumber(total)} matching {noun}
          </button>
        </span>
      )}
      {allMatching && (
        <span style={{ marginLeft: 6 }}>All {formatNumber(total)} {noun} matching the current filter are selected.</span>
      )}
    </div>
  );
}
